<template>
  <div>
    <div v-if="celebrationPopup" class="bl-backdrop-overlay">
      <BlGif gif="celebration" class="rounded-full h-72 w-72" />
    </div>
    <BlBackdrop
      v-else
      :label="`Accept (${selectedRecommendation.length})`"
      :cancelLabel="`Skip recommendations`"
      :disabled="isDisabledAccept"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onAccept"
    >
      <div
        class="flex flex-col gap-8 px-4 pt-4 pb-4 overflow-x-hidden overflow-y-auto lg:pt-8 lg:px-8"
        :style="
          viewMode === 'desktop'
            ? { width: '75rem', maxHeight: 'calc(100vh - 16rem)' }
            : { maxHeight: 'calc(100vh - 6rem)' }
        "
      >
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <div
              class="p-1 bg-orange-300 lg:p-2"
              :style="{ borderRadius: '4px' }"
            >
              <BlIcon icon="tooltip" class="w-6 h-6" />
            </div>
            <BlTextTitle4
              v-if="viewMode === 'desktop'"
              text="Action recommendations: passing success metric"
            />
            <BlTextSubtitle1 v-else text="Passing success metric" />
          </div>
          <div
            v-if="viewMode === 'desktop'"
            class="flex flex-wrap items-center gap-x-1"
          >
            <BlTextBody2
              text="Below are the targets that have successfully reached their success metrics and may change their status to mastered. No worries if you choose to skip the"
            />
            <BlTextBody2
              text="recommendation; you can still find this information on the"
            />
            <BlTextBody2 class="font-bold" text="Recommendations" />
            <BlTextBody2 text="page." />
          </div>
          <div v-else class="space-y-4">
            <div class="flex items-center gap-2">
              <StatusChip status="in_progress" />
              <span class="text-grey-300">→</span>
              <StatusChip status="mastered" />
            </div>
            <div class="flex flex-wrap items-center gap-x-1">
              <BlTextBody2
                text="Below are the targets that have successfully reached their success metrics and may change their status to mastered. No worries if you choose to skip the recommendation;"
              />
              <BlTextBody2 text="you can still find this information on the" />
              <BlTextBody2 class="font-bold" text="Recommendations" />
              <BlTextBody2 text="page." />
            </div>
          </div>
        </div>
        <div v-if="viewMode === 'desktop'">
          <div id="metric-header" class="flex text-grey">
            <div
              class="flex items-start flex-shrink-0 gap-2 px-3 py-2 rounded-l bg-grey-100 w-72"
            >
              <BlInputCheck
                :checked="masterChecked"
                :indeterminate="masterIndeterminate"
                @changed="onChangeSelectedAll"
                class="mt-1"
              />
              <BlTextSubtitle2 text="Target" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-grey-100">
              <BlTextSubtitle2 text="Success metric" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-grey-100">
              <BlTextSubtitle2 text="Total successful sessions" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-grey-100">
              <BlTextSubtitle2 text="Number of consecutive success" />
            </div>
            <div class="flex-shrink-0 w-64 px-3 py-2 rounded-r bg-grey-100">
              <BlTextSubtitle2 text="Recommended action" />
            </div>
          </div>
          <div
            v-for="recommendation in recommendations"
            :key="recommendation.id"
            class="flex"
          >
            <div
              class="flex items-center flex-shrink-0 gap-2 px-3 py-4 border-b w-72 border-grey-200"
              :title="recommendation.target.name"
            >
              <BlInputCheck
                :checked="selectedRecommendation.includes(recommendation.id)"
                @changed="onChangeSelected(recommendation.id)"
              />
              <BlTextBody1
                :text="recommendation.target.name"
                class="truncate text-grey"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 px-3 py-4 border-b border-grey-200"
              :title="generateSuccessMetric(recommendation.target)"
            >
              <BlTextBody1
                :text="generateSuccessMetric(recommendation.target)"
                class="truncate text-grey-400"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 px-3 py-4 border-b border-grey-200"
            >
              <BlTextBody1
                :text="
                  recommendation.target.total_success
                    ? `${recommendation.target.total_success} session(s)`
                    : '-'
                "
                class="truncate text-grey-400"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 px-3 py-4 border-b border-grey-200"
            >
              <BlTextBody1
                :text="
                  recommendation.target.consecutive_success
                    ? `${recommendation.target.consecutive_success} time(s)`
                    : '-'
                "
                class="truncate text-grey-400"
              />
            </div>
            <div
              class="flex items-center justify-between flex-shrink-0 w-64 gap-2 px-3 py-4 border-b border-grey-200"
            >
              <StatusChip :status="recommendation.target.status" />
              <span class="text-grey-300">→</span>
              <StatusChip :status="recommendation.recommended_action" />
            </div>
          </div>
        </div>
        <div v-else id="mobile-popup">
          <div
            v-for="recommendation in recommendations"
            :key="recommendation.id"
            class="flex flex-col py-5 border-b border-grey-200"
          >
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 truncate">
                <BlInputCheck
                  :checked="selectedRecommendation.includes(recommendation.id)"
                  @changed="onChangeSelected(recommendation.id)"
                />
                <BlTextBody2
                  :text="recommendation.target.name"
                  class="truncate text-grey"
                />
              </div>
              <div>
                <BlIcon
                  class="flex-shrink-0 w-6 h-6 transition-all duration-300"
                  icon="chevron-down"
                  clickable
                  @clicked="onOpenDetails(recommendation.id)"
                  :style="
                    detailsOpened.includes(recommendation.id)
                      ? 'rotate: 180deg'
                      : ''
                  "
                  style=""
                />
              </div>
            </div>
            <div
              v-if="detailsOpened.includes(recommendation.id)"
              class="grid grid-cols-2 px-6 py-2 gap-x-4 gap-y-2"
            >
              <BlTextBody2 text="Success metric" class="truncate" />
              <BlTextBody2
                :text="generateSuccessMetric(recommendation.target)"
                class="truncate"
              />
              <BlTextBody2 text="Total successful sessions" class="truncate" />
              <BlTextBody2
                :text="
                  recommendation.target.total_success
                    ? `${recommendation.target.total_success} session(s)`
                    : '-'
                "
                class="truncate"
              />
              <BlTextBody2
                text="Number of consecutive success"
                class="truncate"
              />
              <BlTextBody2
                :text="
                  recommendation.target.consecutive_success
                    ? `${recommendation.target.consecutive_success} time(s)`
                    : '-'
                "
                class="truncate"
              />
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import BlBackdrop from "../../BlBackdrop.vue";
import BlGif from "../../BlGif.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputCheck from "../../BlInputCheck.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import StatusChip from "../../clients/target/StatusChip.vue";

export default {
  name: "RecommendationPopup",
  props: ["recommendations", "showSuccessRecordToast"],
  components: {
    BlBackdrop,
    BlIcon,
    BlTextTitle4,
    BlTextBody2,
    BlTextSubtitle2,
    BlTextBody1,
    StatusChip,
    BlInputCheck,
    BlTextSubtitle1,
    BlGif
  },
  data() {
    return {
      celebrationPopup: false,
      submitLoading: false,
      selectedRecommendation: [],
      detailsOpened: []
    };
  },
  computed: {
    ...mapGetters(["viewMode"]),
    isDisabledAccept() {
      if (this.submitLoading) return true;
      if (!this.selectedRecommendation.length) return true;
      return false;
    },
    masterChecked() {
      if (!this.recommendations) return false;
      return this.recommendations.every(a =>
        this.selectedRecommendation.includes(a.id)
      );
    },
    masterIndeterminate() {
      if (!this.recommendations) return false;
      return (
        this.recommendations.findIndex(a =>
          this.selectedRecommendation.includes(a.id)
        ) > -1 && !this.masterChecked
      );
    }
  },
  mounted() {
    this.celebrationPopup = true;
    this.selectedRecommendation = this.recommendations.map(a => a.id);
    setTimeout(() => {
      this.celebrationPopup = false;
    }, 2000);
  },
  methods: {
    ...mapActions(["acceptActionRecommendation"]),
    generateSuccessMetric(target) {
      let prefix = "";
      let goalText = "";
      let suffix = "";

      // prefix
      if (target.success_metric === "equal to or greater than goal") {
        prefix = "≥ ";
      }
      if (target.success_metric === "less than goal") {
        prefix = "< ";
      }

      // goal text
      if (target.type_name === "Duration") {
        goalText = target.goal_time;
      } else if (
        target.type_name === "Prompting" &&
        target.prompting_format === "classic"
      ) {
        goalText = `≥ ${target.goal}`;
      } else {
        goalText = target.goal;
      }

      // suffix
      if (target.type_name === "Percentage") suffix = "%";
      if (target.type_name === "Partial interval recording") suffix = "%";
      if (
        target.type_name === "Prompting" &&
        target.prompting_format === "custom"
      ) {
        suffix = "%";
      }
      if (target.type_name === "Frequency") suffix = " attempt(s)";
      if (
        target.type_name === "Prompting" &&
        target.prompting_format === "classic"
      ) {
        suffix = ` attempt(s) ${target.success_metric} prompts`;
      }

      return prefix + goalText + suffix;
    },
    onOpenDetails(id) {
      if (this.detailsOpened.includes(id)) {
        this.detailsOpened.splice(this.detailsOpened.indexOf(id), 1);
      } else this.detailsOpened.push(id);
    },
    onChangeSelected(id) {
      if (this.selectedRecommendation.includes(id)) {
        this.selectedRecommendation.splice(
          this.selectedRecommendation.indexOf(id),
          1
        );
      } else this.selectedRecommendation.push(id);
    },
    onChangeSelectedAll() {
      if (this.selectedRecommendation.length === this.recommendations.length) {
        this.selectedRecommendation = [];
      } else if (this.recommendations) {
        this.selectedRecommendation = this.recommendations.map(a => a.id);
      }
    },
    onAccept() {
      this.submitLoading = true;
      this.acceptActionRecommendation({
        ids: this.selectedRecommendation.join(",")
      }).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.$emit("close-modal");
      });
    }
  }
};
</script>

<style scoped lang="scss">
.bl-backdrop-overlay {
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(10px);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/sessions/modal/RecommendationPopup.vue